import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "react-bootstrap";
import { vehicleFileAPI, VehicleFileViewModel } from "../api/vehicleFileAPI";
import { Problem } from "../api/backendApi";
import ErrorDisplay from "../common/ErrorDisplay";
import { Clipboard2Plus } from "react-bootstrap-icons";
import { makeAPI, MakeModel } from "../api/makeAPI";
import { modelInfoApi, ModelInfoModel } from "../api/modelInfoApi";

interface ImportModelInfoModalProps {
  vehicleFileNumber: string;
  onClose: () => void;
  onImport: (imported: ModelInfoModel[]) => void;
}

function ImportModelInfoModal({
  vehicleFileNumber,
  onClose,
  onImport,
}: ImportModelInfoModalProps) {
  const [selectedMake, setSelectedMake] = useState<string>();
  const [makes, setMakes] = useState<MakeModel[]>();
  const [vehicleFiles, setVehicleFiles] = useState<VehicleFileViewModel[]>();
  const [error, setError] = useState<Problem>();

  const loadVehicleFiles = async (makeCode: string) => {
    const result = await vehicleFileAPI.getVehicleFilesByMake(makeCode);

    if (!result.ok) {
      setError(result.problem);
      return;
    }

    setVehicleFiles(
      result.value.filter((vf) => vf.number !== vehicleFileNumber),
    );
    setError(undefined);
  };

  useEffect(() => {
    async function load() {
      const makesResult = await makeAPI.getMakes();

      if (!makesResult.ok) {
        setError(makesResult.problem);
        return;
      }

      const makeCode = vehicleFileNumber.substring(0, 2);

      await loadVehicleFiles(makeCode);

      setSelectedMake(makeCode);
      setMakes(makesResult.value);
      setError(undefined);
    }

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleFileNumber]);

  const handleMakeChanged = async (makeCode: string) => {
    setSelectedMake(makeCode);
    await loadVehicleFiles(makeCode);
  };

  const handleImport = async (number: string) => {
    const result = await modelInfoApi.getModelInfo(number);

    if (!result.ok) {
      setError(result.problem);
      return;
    }

    setError(undefined);

    // recId must be cleared!!
    onImport(result.value.map((mi) => ({ ...mi, recId: undefined })));
  };

  const tableColSpan = 3;

  return (
    <Modal show={true} onHide={() => onClose()} size="lg">
      <ModalHeader>
        <Modal.Title>Import model info from another vehicle file</Modal.Title>
      </ModalHeader>
      <ModalBody>
        <ErrorDisplay error={error} />
        <Row className="my-1 px-2">
          <Form.Label column className="col-auto">
            Make:{" "}
          </Form.Label>
          <Col>
            <Form.Select
              className="w-auto"
              value={selectedMake}
              onChange={(e) => handleMakeChanged(e.target.value)}
            >
              {makes &&
                makes.length > 0 &&
                makes.map((m) => (
                  <option value={m.code} key={m.code}>
                    {m.name}
                  </option>
                ))}
            </Form.Select>
          </Col>
        </Row>

        <div className="fixed-table-header" style={{ height: "400px" }}>
          <Table hover>
            <thead>
              <tr>
                <th>Number</th>
                <th>Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!vehicleFiles && (
                <tr>
                  <td colSpan={tableColSpan}>Loading...</td>
                </tr>
              )}
              {vehicleFiles && vehicleFiles.length === 0 && (
                <tr>
                  <td colSpan={tableColSpan}>No vehicles</td>
                </tr>
              )}
              {vehicleFiles &&
                vehicleFiles.length > 0 &&
                vehicleFiles.map((vf) => (
                  <tr key={vf.number}>
                    <td>{vf.number}</td>
                    <td>{vf.description}</td>
                    <td>
                      <Clipboard2Plus
                        role="button"
                        onClick={() => handleImport(vf.number)}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button size="sm" variant="outline-dark" onClick={() => onClose()}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ImportModelInfoModal;
